import React, { forwardRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { ThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import { Theme } from '@mui/material';
import { AppDispatch } from '@typings/redux';
import { filtersThemeFactory } from 'styles/themes/filtersTheme';
import { Form, SelectBox, DatePicker } from 'components/shared/form';
import { SearchInput } from 'components/shared/form/SearchInput/SearchInput';
import { useStyles } from 'components/institution/Browser/Toolbar/Toolbar.styles';
import { useTranslator } from 'components/shared/hooks/useTranslator/useTranslator';
import { AutoSave } from 'components/shared/form/AutoSave/AutoSave';
import { UserPersonalDataChangeFiltersModel, OriginRole } from '../AccessList.types';
import { userPersonalDataChangeRegisterListSelector } from 'store/reports/reports.selector';
import { setUserPersonalDataChangeRegisterListFilters } from 'store/reports/reports.action';
import { FocusTrap } from 'components/shared/form/FocusTrap/FocusTrap';

export interface ToolbarProps {
  onGridFocus: () => void;
}

const ToolbarComponent = (props: ToolbarProps, ref: React.ForwardedRef<HTMLElement>) => {
  const classes = useStyles();
  const trans = useTranslator();
  const dispatch = useDispatch() as AppDispatch;
  const { filters } = useSelector(userPersonalDataChangeRegisterListSelector);

  const valuesChanged = (values: UserPersonalDataChangeFiltersModel) => {
    dispatch(setUserPersonalDataChangeRegisterListFilters(values));
  };

  return (
    <section ref={ref} className={classes.toolbar}>
      <Form<UserPersonalDataChangeFiltersModel>
        initialValues={filters}
        enableReinitialize={true}
        onSubmit={valuesChanged}
        className={classes.form}
      >
        {() => (
          <div className={classes.filters}>
            <StyledEngineProvider injectFirst>
              <ThemeProvider theme={(parentTheme: Theme) => filtersThemeFactory(parentTheme)}>
                <DatePicker
                  name="dateFrom"
                  labelText={trans('REPORTS.SYNCHRONIZATIONS.FILTERS.DATE_FROM.LABEL')}
                  required
                  clearable={false}
                  disableKeyboard
                />
                <DatePicker
                  name="dateTo"
                  labelText={trans('REPORTS.SYNCHRONIZATIONS.FILTERS.DATE_TO.LABEL')}
                  required
                  clearable={false}
                  disableKeyboard
                />
                <SearchInput
                  name="userLogin"
                  labelText={trans(
                    'PERSONAL_DATA_ACCESS.USER_PERSONAL_DATA_CHANGE_REGISTER.FILTER_TOOLBAR.USER_LOGIN.LABEL',
                  )}
                  resettable
                  tooltipText={trans(
                    'PERSONAL_DATA_ACCESS.USER_PERSONAL_DATA_CHANGE_REGISTER.FILTER_TOOLBAR.USER_LOGIN.SEARCH',
                  )}
                />
                <SelectBox
                  name="origin"
                  placeholderText={trans(
                    'PERSONAL_DATA_ACCESS.USER_PERSONAL_DATA_CHANGE_REGISTER.FILTER_TOOLBAR.ORIGIN.ALL',
                  )}
                  labelText={trans('PERSONAL_DATA_ACCESS.USER_PERSONAL_DATA_CHANGE_REGISTER.FILTER_TOOLBAR.ORIGIN.LABEL')}
                  displayEmpty
                  options={[
                    {
                      value: OriginRole.APPLICANT,
                      label: trans('PERSONAL_DATA_ACCESS.DATA_CHANGE_REGISTER_LIST.COLUMNS.ORIFIN.APPLICANT'),
                    },
                    {
                      value: OriginRole.OFFICIAL,
                      label: trans('PERSONAL_DATA_ACCESS.DATA_CHANGE_REGISTER_LIST.COLUMNS.ORIGIN.OFFICIAL'),
                    },
                  ]}
                />
                <SearchInput
                  name="nameAndLastName"
                  labelText={trans(
                    'PERSONAL_DATA_ACCESS.USER_PERSONAL_DATA_CHANGE_REGISTER.FILTER_TOOLBAR.LAST_NAME.LABEL',
                  )}
                  resettable
                  tooltipText={trans(
                    'PERSONAL_DATA_ACCESS.USER_PERSONAL_DATA_CHANGE_REGISTER.FILTER_TOOLBAR.LAST_NAME.SEARCH',
                  )}
                />
                <AutoSave debounced={['userLogin', 'nameAndLastName']} />
                <FocusTrap handler={() => props.onGridFocus()} />
              </ThemeProvider>
            </StyledEngineProvider>
          </div>
        )}
      </Form>
    </section>
  );
};

ToolbarComponent.displayName = 'Toolbar';
export const Toolbar = forwardRef(ToolbarComponent);
